import { StaticCanvas, FabricImage } from 'fabric';
import { useEffect, useState } from 'react';

import { Adjustment } from 'editor/src/store/design/types';

import useFilters from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Image/getAdjustmentFilters';

let canvas: StaticCanvas;
function getCanvas() {
  if (!canvas) {
    const domCanvas = document.createElement('canvas');
    domCanvas.id = 'filter-preview';
    canvas = new StaticCanvas(domCanvas, {
      backgroundColor: '#EEEEEE',
      renderOnAddRemove: false,
    });
  }

  return canvas;
}

export function useFilterPreview(
  imageUrl: string | undefined,
  adjustments: Adjustment[] | undefined,
  outputWidth: number,
  outputHeight: number,
  mode: 'fit' | 'fill',
  // png keeps transparency so the caller can show its own background behind the image;
  // with 'fit' the output is cropped to the image, so no transparent padding is added around it
  format: 'jpeg' | 'png' = 'jpeg',
) {
  const [imageData, setImageData] = useState('');
  const filters = useFilters(adjustments);

  useEffect(() => {
    if (!imageUrl) {
      return undefined;
    }

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const fabricImage = new FabricImage(image, {
        angle: 0,
        left: 0,
        top: 0,
        objectCaching: false,
        strokeWidth: 0, // avoid wrong size when calling getScaledXxxx()
      });

      const { width, height } = fabricImage.getOriginalSize();
      if (mode === 'fill') {
        if (width > height) {
          fabricImage.scaleToHeight(outputWidth);
        } else {
          fabricImage.scaleToWidth(outputHeight);
        }
      } else if (width > height) {
        fabricImage.scaleToWidth(outputWidth);
      } else {
        fabricImage.scaleToHeight(outputHeight);
      }

      const cropToImage = format === 'png' && mode === 'fit';
      const cropWidth = cropToImage ? Math.round(fabricImage.getScaledWidth()) : outputWidth;
      const cropHeight = cropToImage ? Math.round(fabricImage.getScaledHeight()) : outputHeight;
      fabricImage.set({
        left: -((fabricImage.getScaledWidth() - cropWidth) / 2),
        top: -((fabricImage.getScaledHeight() - cropHeight) / 2),
      });
      fabricImage.applyFilters(filters);
      const fabricCanvas = getCanvas();
      const { backgroundColor } = fabricCanvas;
      if (format === 'png') {
        fabricCanvas.backgroundColor = '';
      }
      fabricCanvas.add(fabricImage);
      fabricCanvas.renderAll();

      const previewData = fabricCanvas.toDataURL({
        multiplier: 1,
        format,
        quality: 0.6,
        enableRetinaScaling: true,
        left: 0,
        top: 0,
        width: cropWidth,
        height: cropHeight,
      });
      fabricImage.dispose();
      fabricCanvas.remove(fabricImage);
      fabricCanvas.backgroundColor = backgroundColor;
      setImageData(previewData);
    };
    image.onerror = () => setImageData(imageUrl);
    image.src = imageUrl;

    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [imageUrl, filters, format]);

  return imageData;
}

export default useFilterPreview;
